import { SEO } from '../../shared/components/SEO';
import { useEffect } from 'react';
import { Award, CheckCircle2, Factory, FileCheck2, HeartHandshake, Leaf, Network, ShieldCheck, Sprout, Store, Truck } from 'lucide-react';
import { CTASection } from '../../shared/components/CTASection';
import { FAQ } from '../../shared/components/FAQ';
import { SectionHeading } from '../../shared/components/SectionHeading';
import { ValueCard } from '../../shared/components/ValueCard';
import { WhereToBuyForm } from '../../shared/components/WhereToBuyForm';
import { ContactForm } from '../../shared/components/ContactForm';
import { farmerFaqs } from '../../shared/data/faqs';
import { PageShell } from '../PageShell/index';

export function QualityResearch() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            const statNumbers = entry.target.querySelectorAll('.zr-stat-number');
            statNumbers.forEach((numberEl) => {
              if (numberEl.getAttribute('data-target')) {
                const target = parseInt(numberEl.getAttribute('data-target') || '0', 10);
                let start = 0;
                const duration = 2000;
                const increment = target / (duration / 16);
                const updateCounter = () => {
                  start += increment;
                  if (start < target) {
                    numberEl.textContent = Math.ceil(start).toString();
                    requestAnimationFrame(updateCounter);
                  } else {
                    numberEl.textContent = target.toString();
                  }
                };
                updateCounter();
                numberEl.removeAttribute('data-target'); // Prevent re-animating
              }
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.zr-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <PageShell 
        title="Quality & Research | ZFAST Agritech India" 
        description="Explore ZFAST Agritech India quality and research approach for cattle feed, pig feed, edible oil enquiries, raw material selection, production control, storage guidance, batch consistency, and dealer documentation." 
        path="/quality-research" 
        hideHeader
        preloadImages={[
          { href: '/images/about/factory-exterior.webp', fetchPriority: 'high' }
        ]}
      >
      <style>{`
:root {
      --forest: #25671e;
      --deep: #0b2217;
      --deep-2: #103d28;
      --leaf: #48a111;
      --gold: #805d13;
      --gold-2: #c69935;
      --cream: #f7f0f0;
      --tint: #eef5b6;
      --white: #ffffff;
      --ink: #243219;
      --muted: rgba(36, 50, 25, 0.72);
      --line: rgba(37, 103, 30, 0.14);
      --shadow: 0 10px 25px rgba(22, 56, 35, 0.08);
      --shadow-lg: 0 18px 55px rgba(22, 56, 35, 0.13);
      --radius: 24px;
      --radius-lg: 34px;
      --container: 1280px;
    }

    * { box-sizing: border-box; }
    html { scroll-behavior: smooth; }
    body {
      margin: 0;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
      color: var(--ink);
      background:
        radial-gradient(circle at 12% 4%, rgba(238, 245, 182, 0.8), transparent 420px),
        linear-gradient(180deg, #fffaf7 0%, #ffffff 38%, #f8fbf7 100%);
    }
    img { max-width: 100%; }
    a { color: inherit; text-decoration: none; }

    .zr-container {
      width: min(var(--container), calc(100% - 32px));
      margin: 0 auto;
    }
    .zr-section { padding: 74px 0; }
    .zr-section-soft { background: linear-gradient(180deg, #f7fbf7 0%, #ffffff 100%); }
    .zr-section-cream { background: var(--cream); }
    .zr-kicker {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: var(--gold);
      font-size: 13px;
      font-weight: 900;
      letter-spacing: 0.16em;
      text-transform: uppercase;
    }
    .zr-kicker::before {
      content: "";
      width: 34px;
      height: 2px;
      border-radius: 99px;
      background: var(--gold);
    }
    .zr-section-head {
      max-width: 840px;
      margin: 0 auto 38px;
      text-align: center;
    }
    .zr-section-head .zr-kicker { justify-content: center; }
    .zr-section-head h2,
    .zr-title h2 {
      margin: 12px 0 0;
      color: var(--forest);
      font-size: clamp(32px, 4vw, 56px);
      line-height: 1.02;
      letter-spacing: -0.045em;
      font-weight: 1000;
    }
    .zr-section-head p,
    .zr-title p {
      margin: 16px 0 0;
      color: var(--muted);
      font-size: 17px;
      line-height: 1.75;
    }

    .zr-hero {
      position: relative;
      overflow: hidden;
      padding: 92px 0 78px;
      background:
        linear-gradient(120deg, rgba(11, 34, 23, 0.95), rgba(37, 103, 30, 0.94)),
        url("${new URL('/images/about/factory-exterior.webp', import.meta.url).href}") center/cover no-repeat;
      color: #fff;
      isolation: isolate;
    }
    .zr-hero::before {
      content: "";
      position: absolute;
      inset: -35%;
      background:
        radial-gradient(circle at 20% 20%, rgba(244, 197, 90, 0.22), transparent 26rem),
        radial-gradient(circle at 80% 30%, rgba(238, 245, 182, 0.16), transparent 28rem);
      animation: zrFloatBg 12s ease-in-out infinite alternate;
      z-index: -1;
    }
    .zr-hero-grid {
      display: grid;
      grid-template-columns: 1.15fr 0.85fr;
      gap: 42px;
      align-items: center;
    }
    .zr-hero h1 {
      margin: 12px 0 0;
      font-size: clamp(44px, 7vw, 90px);
      line-height: 0.95;
      letter-spacing: -0.065em;
      font-weight: 1000;
    }
    .zr-hero p {
      max-width: 790px;
      margin: 24px 0 0;
      color: rgba(255,255,255,0.88);
      font-size: clamp(17px, 2vw, 21px);
      line-height: 1.7;
    }
    .zr-hero-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 30px;
    }
    .zr-hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 11px 15px;
      border-radius: 999px;
      background: rgba(255,255,255,0.1);
      border: 1px solid rgba(255,255,255,0.18);
      color: #fff;
      font-weight: 800;
      backdrop-filter: blur(10px);
    }
    .zr-hero-card {
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.2);
      border-radius: var(--radius-lg);
      padding: 18px;
      backdrop-filter: blur(14px);
      box-shadow: 0 20px 60px rgba(0,0,0,0.16);
      transform: rotate(1deg);
      animation: zrSoftLift 4.5s ease-in-out infinite alternate;
    }
    .zr-hero-card img {
      display: block;
      width: 100%;
      height: 370px;
      object-fit: cover;
      border-radius: 24px;
    }
    .zr-hero-card .zr-note {
      margin: 14px 4px 2px;
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 12px;
      align-items: start;
      color: rgba(255,255,255,0.9);
      line-height: 1.55;
      font-weight: 700;
    }

    .zr-grid { display: grid; gap: 26px; }
    .zr-grid-2 { grid-template-columns: repeat(2, 1fr); }
    .zr-grid-3 { grid-template-columns: repeat(3, 1fr); }
    .zr-grid-4 { grid-template-columns: repeat(4, 1fr); }
    .zr-grid-5 { grid-template-columns: repeat(5, 1fr); }
    .zr-align-center { align-items: center; }

    .zr-card {
      position: relative;
      overflow: hidden;
      background: #fff;
      border: 1px solid var(--line);
      border-radius: var(--radius);
      padding: 28px;
      box-shadow: var(--shadow);
      transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease;
    }
    .zr-card:hover {
      transform: translateY(-6px);
      box-shadow: var(--shadow-lg);
      border-color: rgba(72, 161, 17, 0.28);
    }
    .zr-card h3 {
      margin: 14px 0 0;
      color: var(--forest);
      font-size: 21px;
      line-height: 1.2;
    }
    .zr-card p {
      margin: 12px 0 0;
      color: var(--muted);
      line-height: 1.7;
    }
    .zr-card-icon {
      width: 50px;
      height: 50px;
      border-radius: 17px;
      display: grid;
      place-items: center;
      background: var(--tint);
      color: var(--forest);
      font-weight: 1000;
      font-size: 18px;
    }
    .zr-image-card {
      position: relative;
      overflow: hidden;
      border-radius: var(--radius-lg);
      padding: 12px;
      background: linear-gradient(135deg, var(--forest), var(--leaf));
      box-shadow: var(--shadow-lg);
    }
    .zr-image-card img {
      width: 100%;
      height: 430px;
      object-fit: cover;
      border-radius: 24px;
      display: block;
    }
    .zr-image-label {
      position: absolute;
      left: 28px;
      right: 28px;
      bottom: 28px;
      border-radius: 22px;
      padding: 18px;
      background: rgba(255,255,255,0.94);
      box-shadow: var(--shadow);
    }
    .zr-image-label b {
      display: block;
      color: var(--forest);
      font-size: 20px;
      line-height: 1.25;
    }
    .zr-image-label span {
      display: block;
      margin-top: 6px;
      color: var(--muted);
      line-height: 1.5;
    }
    .zr-pill-row {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 28px;
    }
    .zr-pill {
      background: var(--tint);
      color: var(--forest);
      border: 1px solid rgba(37,103,30,0.16);
      border-radius: 999px;
      padding: 12px 16px;
      font-weight: 900;
    }

    .zr-why-wrap {
      background: linear-gradient(180deg, #f7fbf7 0%, #ffffff 100%);
    }
    .zr-why-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 18px;
    }
    .zr-why-card {
      position: relative;
      overflow: hidden;
      background: linear-gradient(160deg, #103d28, #071b11);
      color: #fff;
      min-height: 245px;
      padding: 26px 20px;
      border-radius: 20px;
      box-shadow: var(--shadow);
      border: 1px solid rgba(255, 255, 255, 0.08);
      transition: transform 0.35s ease, box-shadow 0.35s ease;
    }
    .zr-why-card::after {
      content: "";
      position: absolute;
      inset: auto -20% -30% auto;
      width: 130px;
      height: 130px;
      border-radius: 50%;
      background: rgba(244,197,90,0.08);
      transition: transform .35s ease;
    }
    .zr-why-card:hover {
      transform: translateY(-7px);
      box-shadow: 0 18px 42px rgba(0,0,0,0.13);
    }
    .zr-why-card:hover::after { transform: scale(1.4); }
    .zr-icon {
      width: 56px;
      height: 56px;
      border-radius: 18px;
      background: rgba(184, 138, 42, 0.16);
      color: var(--gold-2);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      border: 1px solid rgba(244, 197, 90, 0.28);
    }
    .zr-icon svg {
      width: 31px;
      height: 31px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .zr-why-card h3 {
      margin: 0 0 10px;
      font-size: 18px;
      color: #fff;
      line-height: 1.2;
    }
    .zr-why-card p {
      margin: 0;
      color: rgba(255,255,255,0.84);
      font-size: 14px;
      line-height: 1.65;
    }

    .zr-facility-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
    }
    .zr-facility-card {
      overflow: hidden;
      background: #fff;
      border-radius: 20px;
      border: 1px solid var(--line);
      box-shadow: var(--shadow);
      transition: transform .35s ease, box-shadow .35s ease;
    }
    .zr-facility-card:hover {
      transform: translateY(-7px);
      box-shadow: var(--shadow-lg);
    }
    .zr-facility-card img {
      display: block;
      width: 100%;
      height: 235px;
      object-fit: cover;
      transition: transform .55s ease;
    }
    .zr-facility-card:hover img { transform: scale(1.06); }
    .zr-facility-content {
      padding: 18px 18px 20px;
      border-top: 5px solid var(--forest);
    }
    .zr-facility-content h3 {
      margin: 0 0 8px;
      color: var(--forest);
      font-size: 19px;
      line-height: 1.25;
    }
    .zr-facility-content p {
      margin: 0;
      color: var(--muted);
      font-size: 14px;
      line-height: 1.65;
    }

    .zr-stats-wrap {
      margin-top: 42px;
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 18px;
    }
    .zr-stat-card {
      background: linear-gradient(160deg, #103d28, #071b11);
      border-radius: 20px;
      padding: 28px 18px;
      text-align: center;
      color: #fff;
      box-shadow: var(--shadow);
      border: 1px solid rgba(255,255,255,0.08);
    }
    .zr-stat-top {
      display: inline-flex;
      align-items: flex-start;
      justify-content: center;
      gap: 2px;
    }
    .zr-stat-number {
      display: inline-block;
      font-size: 40px;
      font-weight: 1000;
      color: var(--gold-2);
      line-height: 1;
    }
    .zr-stat-suffix {
      display: inline-block;
      color: var(--gold-2);
      font-size: 28px;
      font-weight: 1000;
      line-height: 1;
      margin-top: 2px;
    }
    .zr-stat-card p {
      margin: 12px 0 0;
      color: rgba(255,255,255,0.88);
      font-size: 15px;
      font-weight: 700;
    }

    .zr-product-card {
      background: #fff;
      border-radius: var(--radius);
      overflow: hidden;
      border: 1px solid var(--line);
      box-shadow: var(--shadow);
    }
    .zr-product-card img {
      display: block;
      width: 100%;
      height: 230px;
      object-fit: cover;
    }
    .zr-product-card div { padding: 24px; }
    .zr-product-card h3 {
      margin: 0;
      color: var(--forest);
      font-size: 22px;
    }
    .zr-product-card p {
      margin: 12px 0 0;
      color: var(--muted);
      line-height: 1.7;
    }

    .zr-highlight {
      position: relative;
      overflow: hidden;
      border-radius: var(--radius-lg);
      padding: 44px;
      background: linear-gradient(135deg, var(--deep), var(--forest));
      color: #fff;
      box-shadow: var(--shadow-lg);
    }
    .zr-highlight::after {
      content: "";
      position: absolute;
      width: 320px;
      height: 320px;
      right: -130px;
      top: -130px;
      border-radius: 50%;
      background: rgba(244,197,90,0.12);
    }
    .zr-highlight h2 {
      margin: 12px 0 0;
      color: #fff;
      font-size: clamp(30px, 4vw, 52px);
      line-height: 1.05;
      letter-spacing: -0.04em;
    }
    .zr-highlight p {
      max-width: 850px;
      margin: 16px 0 0;
      color: rgba(255,255,255,0.86);
      font-size: 17px;
      line-height: 1.75;
    }
    .zr-mini-list {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-top: 28px;
    }
    .zr-mini-item {
      background: rgba(255,255,255,0.09);
      border: 1px solid rgba(255,255,255,0.14);
      border-radius: 18px;
      padding: 18px;
      backdrop-filter: blur(8px);
    }
    .zr-mini-item b { color: var(--gold-2); }
    .zr-mini-item span {
      display: block;
      margin-top: 8px;
      color: rgba(255,255,255,0.82);
      line-height: 1.55;
    }

    .zr-faq {
      max-width: 960px;
      margin: 34px auto 0;
      display: grid;
      gap: 14px;
    }
    .zr-faq details {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: 20px;
      padding: 20px 22px;
      box-shadow: var(--shadow);
    }
    .zr-faq summary {
      cursor: pointer;
      color: var(--forest);
      font-weight: 1000;
      font-size: 17px;
    }
    .zr-faq p {
      margin: 14px 0 0;
      color: var(--muted);
      line-height: 1.7;
    }

    .zr-reveal {
      opacity: 1;
      transform: none;
      transition: opacity .75s ease, transform .75s ease;
    }
    .zr-reveal.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
    .zr-delay-1 { transition-delay: .08s; }
    .zr-delay-2 { transition-delay: .16s; }
    .zr-delay-3 { transition-delay: .24s; }
    .zr-delay-4 { transition-delay: .32s; }
    .zr-delay-5 { transition-delay: .40s; }

    @keyframes zrFloatBg {
      from { transform: translate3d(-1.5%, -1%, 0) scale(1); }
      to { transform: translate3d(1.5%, 1%, 0) scale(1.04); }
    }
    @keyframes zrSoftLift {
      from { transform: rotate(1deg) translateY(0); }
      to { transform: rotate(-0.5deg) translateY(-10px); }
    }

    @media (max-width: 1120px) {
      .zr-hero-grid,
      .zr-grid-2 { grid-template-columns: 1fr; }
      .zr-grid-4,
      .zr-facility-grid { grid-template-columns: repeat(2, 1fr); }
      .zr-why-grid,
      .zr-stats-wrap { grid-template-columns: repeat(3, 1fr); }
      .zr-hero-card { transform: none; }
      .zr-hero-card img { height: 330px; }
    }
    @media (max-width: 820px) {
      .zr-section { padding: 54px 0; }
      .zr-hero { padding: 68px 0 58px; }
      .zr-grid-3,
      .zr-grid-4,
      .zr-why-grid,
      .zr-facility-grid,
      .zr-stats-wrap,
      .zr-mini-list { grid-template-columns: 1fr; }
      .zr-card,
      .zr-highlight { padding: 24px; }
      .zr-image-card img { height: 320px; }
      .zr-image-label { left: 22px; right: 22px; bottom: 22px; }
      .zr-facility-card img,
      .zr-product-card img { height: 220px; }
      .zr-stat-number { font-size: 36px; }
    }
    @media (prefers-reduced-motion: reduce) {
      * { animation: none !important; transition: none !important; scroll-behavior: auto !important; }
      .zr-reveal { opacity: 1; transform: none; }
    }
      `}</style>

    <section className="zr-hero">
      <div className="zr-container zr-hero-grid">
        <div className="zr-reveal">
          <span className="zr-kicker">Quality assurance</span>
          <h1>Quality &amp; Research</h1>
          <p>
            ZFAST Agritech India follows a quality-first approach for animal nutrition and allied products,
            with attention to raw material selection, production discipline, practical research, storage guidance,
            packaging checks, and dealer documentation support.
          </p>
          <div className="zr-hero-badges">
            <span className="zr-hero-badge">✓ Animal feed quality</span>
            <span className="zr-hero-badge">✓ Scientific formulations</span>
            <span className="zr-hero-badge">✓ Farmer focused</span>
          </div>
        </div>
        <div className="zr-hero-card zr-reveal zr-delay-2">
          <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src={new URL("/images/about/quality-01.webp", import.meta.url).href} alt="ZFAST quality control lab for animal feed quality and research" loading="eager" width={600} height={370} />
          <div className="zr-note">
            <span>●</span>
            <span>Better inputs, controlled process, and useful field feedback help build product confidence.</span>
          </div>
        </div>
      </div>
    </section>

    <section className="zr-section zr-section-soft" id="feeds">
      <div className="zr-container zr-grid zr-grid-2 zr-align-center">
        <div className="zr-title zr-reveal">
          <span className="zr-kicker">ZFAST Feeds &amp; Supplements</span>
          <h2>The complete animal health care solution.</h2>
          <h3 style={{ fontSize: '1.2rem', color: 'var(--ink)', marginBottom: '1rem', fontWeight: 800 }}>‘Health is Wealth.’ A wise dairy farmer will vouch for that.</h3>
          <p>
            Science, innovation and native understanding of dairy business come together to create optimum nutrition solutions. Feeds are built with carefully chosen source ingredients to support animals through all life stages, while supplements provide necessary care.
          </p>
          <div style={{ marginTop: '28px' }}>
            <a className="zr-pill" style={{ display: 'inline-block', background: 'var(--forest)', color: '#fff', border: 'none', textDecoration: 'none' }} href="#aflatoxin">Here’s how we do it!</a>
          </div>
        </div>
        <div className="zr-image-card zr-reveal zr-delay-2" style={{ background: 'transparent', boxShadow: 'none', padding: 0, overflow: 'visible' }}>
          <img className="w-full object-contain" style={{ height: 'auto', filter: 'drop-shadow(0 22px 30px rgba(0,0,0,.18))' }} src={new URL("/images/farmer-corner/zfast-farmer-corner-hero.webp", import.meta.url).href} alt="Winning dairy cow with farmers" loading="lazy" />
        </div>
      </div>
    </section>


    <section className="zr-section zr-section-cream">
      <div className="zr-container">
        <div className="zr-section-head zr-reveal">
          <span className="zr-kicker">Quality pillars</span>
          <h2>The checks that support consistent product confidence.</h2>
          <p>
            Our quality system is written in clear language so farmers, dealers, distributors, and institutional buyers
            can understand what matters before a product reaches the field or shelf.
          </p>
        </div>
        <div className="zr-grid zr-grid-3">
          <article className="zr-card zr-reveal zr-delay-1"><div className="zr-card-icon">01</div><h3>Ingredient Selection</h3><p>Raw materials are reviewed for suitability, freshness, storage condition, and product-fit before they move into production.</p></article>
          <article className="zr-card zr-reveal zr-delay-2"><div className="zr-card-icon">02</div><h3>Quality Checks</h3><p>Checks may include visual inspection, moisture awareness, specification review, packaging readiness, and dispatch suitability.</p></article>
          <article className="zr-card zr-reveal zr-delay-3"><div className="zr-card-icon">03</div><h3>Batch Consistency</h3><p>Repeatable routines help maintain reliable cattle feed, pig feed, and trade supply conversations batch after batch.</p></article>
          <article className="zr-card zr-reveal zr-delay-1"><div className="zr-card-icon">04</div><h3>Storage Guidance</h3><p>Feed and oil products need clean, dry, covered, and well-managed storage to protect quality after dispatch.</p></article>
          <article className="zr-card zr-reveal zr-delay-2"><div className="zr-card-icon">05</div><h3>Feedback Loop</h3><p>Farmer, dealer, and buyer feedback helps the team understand real field use, seasonal needs, and communication gaps.</p></article>
        </div>
      </div>
    </section>

    <section className="zr-section">
      <div className="zr-container zr-grid zr-grid-2 zr-align-center">
        <div className="zr-image-card zr-reveal">
          <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src={new URL("/images/about/quality-03.webp", import.meta.url).href} alt="Cattle feed nutrition research and animal feed quality by ZFAST Agritech India" loading="lazy" width={600} height={430} />
        </div>
        <div className="zr-title zr-reveal zr-delay-2">
          <span className="zr-kicker">Research approach</span>
          <h2>Practical research connected to Indian farming conditions.</h2>
          <p>
            ZFAST focuses on research that can be useful in real farm and dealer discussions. Instead of unclear claims,
            our approach considers nutrition needs, ingredient behaviour, animal condition, storage, seasonal changes,
            and field feedback.
          </p>
          <div className="zr-grid zr-grid-2" style={{ marginTop: "28px" }}>
            <article className="zr-card"><div className="zr-card-icon">A</div><h3>Animal Condition Review</h3><p>Understanding body condition, feeding routine, water access, roughage quality, and seasonal farm challenges.</p></article>
            <article className="zr-card"><div className="zr-card-icon">B</div><h3>Nutrition Positioning</h3><p>Keeping cattle feed and pig feed communication clear around energy, digestion support, growth stage, and daily use.</p></article>
            <article className="zr-card"><div className="zr-card-icon">C</div><h3>Field Feedback</h3><p>Listening to farmers and dealers to understand product experience, questions, training needs, and local conditions.</p></article>
            <article className="zr-card"><div className="zr-card-icon">D</div><h3>Continuous Improvement</h3><p>Using repeatable observations to improve product notes, storage advice, dealer education, and buyer support.</p></article>
          </div>
        </div>
      </div>
    </section>

    <section className="zr-section zr-why-wrap" id="why-choose-zfast">
      <div className="zr-container">
        <div className="zr-section-head zr-reveal">
          <span className="zr-kicker">Why choose ZFAST?</span>
          <h2>Quality. Science. Results.</h2>
          <p>
            At ZFAST Agritech India, we combine dependable ingredients, disciplined manufacturing,
            practical nutrition thinking, and strict quality-focused processes to support better performance
            for farmers, dealers, and distribution partners.
          </p>
        </div>

        <div className="zr-why-grid">
          <article className="zr-why-card zr-reveal zr-delay-1">
            <div className="zr-icon"><svg viewBox="0 0 24 24"><path d="M12 3l2.4 3.6 4.2.9-2.8 3.3.4 4.3L12 13.4 7.8 15.1l.4-4.3-2.8-3.3 4.2-.9L12 3z"/><path d="M8 17l-1 4 5-2 5 2-1-4"/></svg></div>
            <h3>Premium Ingredients</h3>
            <p>We source quality raw materials to support consistent nutrition and better performance across every batch.</p>
          </article>
          <article className="zr-why-card zr-reveal zr-delay-2">
            <div className="zr-icon"><svg viewBox="0 0 24 24"><path d="M3 21h18"/><path d="M5 21V9l5 3V9l5 3V5h4v16"/><path d="M8 17h1M12 17h1M16 17h1"/></svg></div>
            <h3>Advanced Technology</h3>
            <p>Modern manufacturing systems help improve production control, efficiency, and reliability.</p>
          </article>
          <article className="zr-why-card zr-reveal zr-delay-3">
            <div className="zr-icon"><svg viewBox="0 0 24 24"><path d="M9 3h6"/><path d="M10 3v5l-5 9a3 3 0 0 0 2.6 4.5h8.8A3 3 0 0 0 19 17l-5-9V3"/><path d="M8 16h8"/></svg></div>
            <h3>Scientific Formulations</h3>
            <p>Products are developed with balanced nutrition thinking for animal health, milk yield, and productivity.</p>
          </article>
          <article className="zr-why-card zr-reveal zr-delay-4">
            <div className="zr-icon"><svg viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 5-3 8.5-7 10-4-1.5-7-5-7-10V6l7-3z"/><path d="M9 12l2 2 4-5"/></svg></div>
            <h3>Quality Assurance</h3>
            <p>Every batch is handled with strict quality checks to support safety, purity, and consistency.</p>
          </article>
          <article className="zr-why-card zr-reveal zr-delay-5">
            <div className="zr-icon"><svg viewBox="0 0 24 24"><path d="M16 11a4 4 0 1 0-8 0"/><path d="M3 21a7 7 0 0 1 14 0"/><path d="M17 8a3 3 0 0 1 0 6"/><path d="M21 21a5 5 0 0 0-4-5"/></svg></div>
            <h3>Farmer Focused</h3>
            <p>We understand farmer needs and provide practical solutions that improve productivity and profitability.</p>
          </article>
        </div>

        <div className="zr-reveal" id="aflatoxin" style={{ marginTop: '70px', background: '#f8fbff', border: '1px solid var(--line)', borderRadius: '34px', padding: '34px', boxShadow: 'var(--shadow)' }}>
          <div className="zr-grid zr-grid-2 zr-align-center">
            <div className="zr-image-card" style={{ background: '#fff', padding: '20px', borderRadius: '26px', boxShadow: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img className="object-contain" style={{ width: '100%', maxWidth: '550px', height: 'auto' }} src="/images/about/zfast-toxin.webp" alt="Aflatoxin controlled cattle feed" loading="lazy" />
            </div>
            <div className="zr-title">
              <span className="zr-kicker">Quality Control</span>
              <h2>India’s first Afla-Toxin controlled cattle feed.</h2>
              <p>
                Source ingredients are rigorously tested for Afla-Toxin. This quality control allows only the safest ingredients to go further into making feeds, giving farmers better value and helping place a healthier glass of milk on the consumer’s table.
              </p>
              <div style={{ marginTop: '28px' }}>
                <a className="zr-pill" style={{ display: 'inline-block', background: 'var(--forest)', color: '#fff', border: 'none', textDecoration: 'none' }} href="/products">Know More!</a>
              </div>
            </div>
          </div>
        </div>
        <div className="zr-section-head zr-reveal" style={{ marginTop: "70px" }}>
          <span className="zr-kicker">Our World-Class Facility</span>
          <h2>Built for Quality, Consistency &amp; Scale</h2>
          <p>
            Our facility infrastructure supports organized production, better process handling, quality checks,
            and hygienic storage so products reach the market with reliability and confidence.
          </p>
        </div>

        <div className="zr-facility-grid">
          <article className="zr-facility-card zr-reveal zr-delay-1">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/about/quality-05.webp" alt="ZFAST manufacturing plant" loading="lazy" width={400} height={235} />
            <div className="zr-facility-content"><h3>Manufacturing Plant</h3><p>A well-organized production environment designed for efficient and reliable operations.</p></div>
          </article>
          <article className="zr-facility-card zr-reveal zr-delay-2">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/about/quality-06.webp" alt="ZFAST advanced production line" loading="lazy" width={400} height={235} />
            <div className="zr-facility-content"><h3>Advanced Production Line</h3><p>Streamlined production systems help maintain process consistency and output quality.</p></div>
          </article>
          <article className="zr-facility-card zr-reveal zr-delay-3">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/about/quality-07.webp" alt="ZFAST quality control laboratory" loading="lazy" width={400} height={235} />
            <div className="zr-facility-content"><h3>Quality Control Lab</h3><p>Routine checks and testing support quality monitoring and dependable standards.</p></div>
          </article>
          <article className="zr-facility-card zr-reveal zr-delay-4">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/about/quality-08.webp" alt="ZFAST hygienic storage facility" loading="lazy" width={400} height={235} />
            <div className="zr-facility-content"><h3>Hygienic Storage Facility</h3><p>Clean and organized storage practices help protect product quality before dispatch.</p></div>
          </article>
        </div>

        <div className="zr-stats-wrap">
          <div className="zr-stat-card zr-reveal zr-delay-1"><div className="zr-stat-top"><span className="zr-stat-number" data-target="5000">0</span><span className="zr-stat-suffix">+</span></div><p>Happy Farmers</p></div>
          <div className="zr-stat-card zr-reveal zr-delay-2"><div className="zr-stat-top"><span className="zr-stat-number" data-target="100">0</span><span className="zr-stat-suffix">+</span></div><p>Dealers &amp; Distributors</p></div>
          <div className="zr-stat-card zr-reveal zr-delay-3"><div className="zr-stat-top"><span className="zr-stat-number" data-target="15">0</span><span className="zr-stat-suffix">+</span></div><p>Quality Products</p></div>
          <div className="zr-stat-card zr-reveal zr-delay-4"><div className="zr-stat-top"><span className="zr-stat-number" data-target="10">0</span><span className="zr-stat-suffix">+</span></div><p>States Presence</p></div>
          <div className="zr-stat-card zr-reveal zr-delay-5"><div className="zr-stat-top"><span className="zr-stat-number" data-target="100">0</span><span className="zr-stat-suffix">%</span></div><p>Quality Assured</p></div>
        </div>
      </div>
    </section>

    <section className="zr-section zr-section-soft">
      <div className="zr-container">
        <div className="zr-section-head zr-reveal">
          <span className="zr-kicker">Product quality focus</span>
          <h2>Clear quality communication for every category.</h2>
          <p>
            ZFAST quality content supports farmers, dealers, and buyers with simple product understanding,
            responsible handling, and practical usage guidance.
          </p>
        </div>
        <div className="zr-grid zr-grid-3">
          <article className="zr-product-card zr-reveal zr-delay-1">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/about/quality-09.webp" alt="ZFAST cattle feed quality and dairy animal nutrition" loading="lazy" width={400} height={230} />
            <div><h3>Cattle Feed Quality</h3><p>Balanced cattle feed communication focuses on energy, protein, minerals, daily ration support, body condition, milk performance, and clean storage.</p></div>
          </article>
          <article className="zr-product-card zr-reveal zr-delay-2">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/products/pig-feed/zfast-pig-feed-swine-nutrition.webp" alt="ZFAST pig feed manufacturing quality and process control" loading="lazy" width={400} height={230} />
            <div><h3>Pig Feed Quality</h3><p>Pig feed quality depends on ingredient suitability, growth-stage nutrition thinking, batch discipline, packaging readiness, and practical farm guidance.</p></div>
          </article>
          <article className="zr-product-card zr-reveal zr-delay-3">
            <img className="w-full h-auto rounded-[1.25rem] object-cover shadow-sm" src="/images/products/edible-oils/zfast-edible-oils-mustard-soybean-sunflower.webp" alt="ZFAST edible oil storage and hygienic handling" loading="lazy" width={400} height={230} />
            <div><h3>Edible Oil Enquiries</h3><p>For edible oil enquiries, attention is given to product identity, packaging condition, storage handling, freshness communication, and trade documentation.</p></div>
          </article>
        </div>
      </div>
    </section>

    <section className="zr-section">
      <div className="zr-container">
        <div className="zr-highlight zr-reveal">
          <span className="zr-kicker">Safety &amp; storage awareness</span>
          <h2>Aflatoxin awareness, moisture control, and clean storage matter.</h2>
          <p>
            Animal feed quality can be affected by moisture, poor storage, damaged packaging, pest exposure, and unsafe handling.
            ZFAST encourages clean, dry, covered storage practices and responsible stock rotation so farmers and dealers can protect product quality after dispatch.
          </p>
          <div className="zr-mini-list">
            <div className="zr-mini-item"><b>Keep feed dry</b><span>Store bags away from floor dampness, rainwater, and direct moisture contact.</span></div>
            <div className="zr-mini-item"><b>Protect from pests</b><span>Keep storage clean, covered, ventilated, and away from contamination sources.</span></div>
            <div className="zr-mini-item"><b>Use older stock first</b><span>Follow FIFO handling and check packaging condition before farm use.</span></div>
          </div>
        </div>
      </div>
    </section>


    <section className="zr-section">
      <div className="zr-container">
        <div className="zr-section-head zr-reveal">
          <span className="zr-kicker">FAQ</span>
          <h2>Quality &amp; Research FAQs</h2>
          <p>Common questions from farmers, dealers, distributors, and buyers about ZFAST quality and research.</p>
        </div>
        <div className="zr-faq">
          <details className="zr-reveal zr-delay-1" open>
            <summary>What makes ZFAST quality-focused?</summary>
            <p>ZFAST focuses on raw material selection, production discipline, quality checks, storage guidance, packaging readiness, and product documentation so farmers, dealers, and buyers can trust the product journey.</p>
          </details>
          <details className="zr-reveal zr-delay-2">
            <summary>Why is research important in animal feed?</summary>
            <p>Research helps connect ingredient choice, nutrition balance, animal condition, seasonal farm needs, field feedback, and product communication for cattle feed and pig feed users.</p>
          </details>
          <details className="zr-reveal zr-delay-3">
            <summary>Does ZFAST provide quality documentation?</summary>
            <p>Available product information, specifications, labels, batch details, and quality-related documentation can be shared with dealers, distributors, and institutional buyers according to product category and requirement.</p>
          </details>
          <details className="zr-reveal zr-delay-4">
            <summary>How should animal feed be stored?</summary>
            <p>Animal feed should be stored in a dry, clean, covered, and ventilated area, away from direct moisture, pests, rain, and floor dampness. Always follow product pack guidance and use older stock first.</p>
          </details>
        </div>
      </div>
    </section>
  </PageShell>
    </>
  );
}
